import { gql } from '@apollo/client';

import {
  WITH_COUNT_VARIABLES,
  WITH_COUNT_ARGUMENTS
} from '../../queries/withCount';

const ERRORS = `errors { path, message }`;

export const CORE_ASSET_FIELDS = `
  id
  year
  make
  model
  name
  vin
  lastSix
  stockNumber
  clientNumber
  condition
  category
  status
  miles
  subCategory
  engineMfr
  engineType
  transmission
  listPrice
  axle
  sleeper
  suspension
  horsepower
  ratio
  wheelsRear
  wheelsFront
  wheelBase
  color
  mechanicalCondition
  expectedYieldPrice
  inspectionUrl
  securedOn
  notes
  street
  city
  state
  postalCode { formatted, value }
  acquired
  createdAt
  updatedAt
  soarrPhotoUrls
  cachedTitleStatus
  sellerNotes
  drivable
  uccExpiresOn
`;

const ASSET_FIELDS = `
  ${CORE_ASSET_FIELDS}
  sellerFeesTotal
  soldSaleAsset {
    id
    sellerNet
    sellerAchOn
  }
  location {
    id
    street
    city
    state
    postalCode { formatted, value }
    phoneNumber { formatted, value }
  }
  consignmentLocation {
    id
    street
    city
    state
    postalCode { formatted, value }
    phoneNumber { formatted, value }
  }
  photos {
    id
    filename
    contentType
    url
  }
  latestTitleLog {
    id
  }
  seller {
    clientNumberLabel
    clientNumberLabelFull
  }
  titleLogs {
    id
    status
    notes
    upload { id, filename, contentType, url}
    titleNumber
    state
    latestUpload { url }
  }
  status
  buyer { id, name, street, city, state, postalCode { formatted } }
  soldOn
  soldAmount
  consignmentMethod
  customConsignmentAddress
  assetSellerUsers {
    id
    name
    sellerUser {id, fullName}
  }
  documents {
    id
    filename
    contentType
    url
  }
  sellerDocuments {
    id
    filename
    contentType
    url
  }
  sellerDocumentNotes { id, attachmentId, body }
  correctedLotNumber
  createdByType
  createdByName
`;

export const ASSETS_WITH_COUNT = gql`
  query (
    ${WITH_COUNT_VARIABLES}
  ) {
    assetsWithCount(
      ${WITH_COUNT_ARGUMENTS}
    ) {
      entries {
        ${ASSET_FIELDS}
      }
      count
    }
  }
`;

export const ASSET = gql`
  query Asset($id: ID!) {
    asset(id: $id) {
      ${ASSET_FIELDS}
      activeSaleAsset {
        correctedLotNumber
        status
        sale { saleOn, name }
      }
    }
  }
`;

export const UPCOMING_SALE_ASSETS = gql`
  query UpcomingSaleAssets {
    upcomingSaleAssets {
      id
      correctedLotNumber
      sale { id, saleOn }
      asset {
        ${CORE_ASSET_FIELDS}
        seller { id }
        photos { id, url }
      }
    }
  }
`;

const ASSET_VARIABLES = `
  $clientNumber: String
  $sellerUserIds: [ID!]
  $sellerNotes: String
  $consignmentMethod: String
  $drivable: Boolean
  $locationId: ID
  $consignmentLocationId: ID
  $customConsignmentAddress: String
  $uccExpiresOn: ISO8601Date
`;

const ASSET_ARGUMENTS = `
  clientNumber: $clientNumber
  sellerUserIds: $sellerUserIds
  sellerNotes: $sellerNotes
  consignmentMethod: $consignmentMethod
  drivable: $drivable
  locationId: $locationId
  consignmentLocationId: $consignmentLocationId
  customConsignmentAddress: $customConsignmentAddress
  uccExpiresOn: $uccExpiresOn
`;

const ASSET_FIELDS_AND_ERRORS = `
  asset {
    ${ASSET_FIELDS}
  }
  ${ERRORS}
`;

export const CREATE_ASSET = gql`
  mutation CreateAsset(
    $sellerId: ID!
    $year: Int!
    $make: String!
    $model: String!
    $vin: String
    $sellerDocuments: [Upload!]
    $sellerDocumentNotes: [String!]
    $customLocation: Boolean
    $street: String
    $city: String
    $state: String
    $postalCode: String
    ${ASSET_VARIABLES}
  ) {
    createAsset(
      sellerId: $sellerId
      year: $year
      make: $make
      model: $model
      vin: $vin
      sellerDocuments: $sellerDocuments
      sellerDocumentNotes: $sellerDocumentNotes
      customLocation: $customLocation
      street: $street
      city: $city
      state: $state
      postalCode: $postalCode
      ${ASSET_ARGUMENTS}
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const UPDATE_ASSET = gql`
  mutation UpdateAsset(
    $id: ID!
    ${ASSET_VARIABLES}
    $sellerDocuments: [Upload!]
    $sellerDocumentNotes: [String!]
    $customLocation: Boolean
    $street: String
    $city: String
    $state: String
    $postalCode: String
  ) {
    updateAsset(
      id: $id
      ${ASSET_ARGUMENTS}
      sellerDocuments: $sellerDocuments
      sellerDocumentNotes: $sellerDocumentNotes
      customLocation: $customLocation
      street: $street
      city: $city
      state: $state
      postalCode: $postalCode
    ) {
      ${ASSET_FIELDS_AND_ERRORS}
    }
  }
`;

export const SELLER_USER_SELECT_OPTIONS = gql`
  query NoUserLogSelectOptions {
    sellerUsers(scopes: ["notifications"]) {
      id
      fullName
    }
  }
`;

export const VIN_TAKEN = gql`
  query VinTaken($vin: String!, $exceptAssetId: ID) {
    vinTaken(vin: $vin, exceptAssetId: $exceptAssetId)
  }
`;

export const SELECTS = gql`
  query NearbyLocations($address: String) {
    nearbyLocations(address: $address) {
      locations {
        id
        name
        address
        stateId
      }
      distances
    }
  }
`

export const LOCATION_SELECTS = gql`
  query Locations {
    locations (scopes: ["web_visible"]) {
      id
      name
      address
      stateId
    }
  }
`
